"use client";

import Image from "next/image";

import Link from "next/link";

import { motion } from "framer-motion";

import { ArrowRight, PlayCircle, ShieldCheck, Wifi, ScreenShare } from "lucide-react";

export default function Hero() {

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-accent/70 via-white to-white">

      {/* ambient background shapes */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl animate-float-slow sm:h-96 sm:w-96" />
        <div className="absolute top-1/3 -right-32 h-72 w-72 rounded-full bg-primary/[0.08] blur-3xl animate-float sm:h-[28rem] sm:w-[28rem]" />
        <div className="absolute bottom-0 left-1/3 h-56 w-56 rounded-full bg-accent blur-3xl sm:h-72 sm:w-72" />
        <svg className="absolute inset-0 h-full w-full opacity-[0.35]" aria-hidden="true">
          <defs>
            <pattern id="grid" width="44" height="44" patternUnits="userSpaceOnUse">
              <path d="M 44 0 L 0 0 0 44" fill="none" stroke="#E5E7EB" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="container-px mx-auto grid max-w-7xl items-center gap-10 pb-14 pt-10 sm:gap-12 sm:pb-16 sm:pt-14 lg:grid-cols-2 lg:gap-16 lg:pb-28 lg:pt-24">
        <motion.div

          initial={{ opacity: 0, y: 24 }}

          animate={{ opacity: 1, y: 0 }}

          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}

          className="text-center lg:text-left"
        >
          <span className="eyebrow">
            <ShieldCheck className="h-3.5 w-3.5" />  User permission required for every connection
          </span>

          <h1 className="mt-6 text-3xl font-bold leading-[1.15] tracking-tight text-ink sm:text-4xl sm:leading-[1.1] md:text-5xl lg:text-[3.4rem] lg:leading-[1.08]">
            Connect. View.{" "}
            <span className="bg-gradient-to-r from-primary to-orange-400 bg-clip-text text-transparent">
              Control.
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-muted sm:text-lg lg:mx-0">
            The User Application first grants permission, then the Master Application connects to the User device. Once connected, the Master can view the User's screen in real time and remotely control the device.
          </p>

          <div className="mt-8 flex flex-col items-center gap-4 sm:mt-9 sm:flex-row sm:justify-center lg:justify-start">
            <Link href="/contact" className="btn-primary w-full justify-center sm:w-auto">

              Book Demo <ArrowRight className="h-4 w-4" />
            </Link>
            <Link href="/features" className="btn-secondary w-full justify-center sm:w-auto">
              <PlayCircle className="h-4 w-4" /> See How It Works
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-4 border-t border-hairline pt-6 text-sm text-muted lg:justify-start">
            <div>
              <p className="text-2xl font-bold text-ink">50K+</p>
              <p>Sessions run</p>
            </div>
            <div className="hidden h-8 w-px bg-hairline sm:block" />
            <div>
              <p className="text-2xl font-bold text-ink">99.9%</p>
              <p>Uptime</p>
            </div>
            <div className="hidden h-8 w-px bg-hairline sm:block" />
            <div>
              <p className="text-2xl font-bold text-ink">25+</p>
              <p>Countries</p>
            </div>
          </div>
        </motion.div>

        {/* User App grants permission -> Master App connects -> Master views and controls User */}
        <motion.div

          initial={{ opacity: 0, scale: 0.94 }}

          animate={{ opacity: 1, scale: 1 }}

          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}

          className="relative mx-auto flex w-full max-w-xs items-center justify-center gap-3 py-4 sm:max-w-md sm:gap-4 sm:py-6 md:gap-6 lg:max-w-lg lg:gap-8 xl:max-w-xl"
        >
          <PhoneMockup

            label="User App"

            sublabel="User Device"

            screenImage="/images/pair1.jpeg"

            className="animate-float"

            style={{ animationDelay: "1.2s" }}

          />

          {/* connection link */}
          <div className="relative flex flex-shrink-0 flex-col items-center gap-2">
            <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3">
              <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-primary" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary sm:h-3 sm:w-3" />
            </span>
            <Wifi className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
            <div className="h-10 w-px bg-gradient-to-b from-primary/60 to-transparent sm:h-16 md:h-20 lg:h-24" />
            <span className="whitespace-nowrap rounded-full border border-hairline bg-white px-2 py-1 text-[9px] font-semibold text-muted shadow-sm sm:px-2.5 sm:text-[10px]">

              Encrypted
            </span>
          </div>
          <PhoneMockup

            label="Master App"

            sublabel="Master Device"

            accent

            screenImage="/images/connect.png"

            className="animate-float"

          />

        </motion.div>
      </div>
    </section>

  );

}


function PhoneMockup({ label, sublabel, screenImage, accent, className = "", style, }) {

  return (
    <div className={className} style={style}>
      <div className="relative mx-auto w-24 sm:w-32 md:w-36 lg:w-40 xl:w-48">

        {/* Shadow */}
        <div className="absolute inset-0 translate-y-5 scale-95 rounded-[3rem] bg-black/20 blur-2xl" />

        {/* Phone Body */}
        <div className="relative rounded-[1.5rem] bg-gradient-to-b from-zinc-800 via-black to-zinc-900 p-[4px] shadow-[0_20px_50px_rgba(0,0,0,0.35)] sm:rounded-[2.5rem] sm:p-[5px] lg:rounded-[3rem]">

          {/* Metallic Border */}
          {/* <div className="relative rounded-[1.7rem] border border-white/10 bg-zinc-900 p-[4px] sm:rounded-[2.2rem] sm:p-[6px] lg:rounded-[2.7rem]"> */}

          {/* Camera Dot */}
          <div className="absolute left-1/2 top-2 z-20 h-1 w-1 -translate-x-1/2 rounded-full bg-zinc-900 sm:top-3 sm:h-1.5 sm:w-1.5" />

          {/* Camera */}
          {/* <div className="absolute left-1/2 top-2 z-20 ml-6 h-2 w-2 -translate-x-1/2 rounded-full bg-black ring-2 ring-zinc-700 sm:top-3 sm:ml-9 sm:h-2.5 sm:w-2.5">
              <div className="absolute inset-[2px] rounded-full bg-zinc-500 sm:inset-[3px]" />
            </div> */}

          {/* Screen */}
          <div className="relative aspect-[9/19.5] overflow-hidden rounded-[1.4rem] bg-black sm:rounded-[1.9rem] lg:rounded-[2.3rem]">
            <Image
              src={screenImage}
              alt={`${label} Screen`}
              fill
              sizes="(max-width: 640px) 96px, (max-width: 768px) 128px, (max-width: 1024px) 144px, 192px"
              className="object-cover rounded-[1.4rem] sm:rounded-[1.9rem] lg:rounded-[2.3rem]"
            />
          </div>

          {/* Bottom Indicator */}
          <div className="absolute bottom-2 left-1/2 h-1 w-16 -translate-x-1/2 rounded-full bg-white/20 sm:bottom-3 sm:h-1.5 sm:w-24" />

          {/* Left Buttons */}
          <div className="absolute -left-[3px] top-16 h-4 w-[2px] rounded-r bg-zinc-600 sm:-left-[4px] sm:top-24 sm:h-10 sm:w-[3px]" />
          <div className="absolute -left-[3px] top-22 h-4 w-[2px] rounded-r bg-zinc-600 sm:-left-[4px] sm:top-40 sm:h-10 sm:w-[3px]" />

          {/* Right Button */}
          <div className="absolute -right-[3px] top-24 h-6 w-[2px] rounded-l bg-zinc-600 sm:-right-[4px] sm:top-36 sm:h-16 sm:w-[3px]" />
          {/* </div> */}
        </div>
      </div>

      <div className="mt-3 text-center sm:mt-5">
        <h4

          className={`text-xs font-bold sm:text-sm ${accent ? "text-primary" : "text-primary"

            }`}
        >

          {label}
        </h4>

        <p className="text-[10px] text-gray-500 sm:text-xs">{sublabel}</p>
      </div>
    </div>

  );

}
